import { api } from '@/scripts/api';
import { helpers } from '@/scripts/helpers';
import { LandingPageFormSubmissionService } from '@/scripts/services/landing-page/landing-page-form-submission-service';
import moment from 'moment';
import store from '@/stores';

export const ActivitiesService = {
    isActivityMeetingType(activity) {
        return activity.type.value === 'Meeting';
    },

    getActivityStartDateTime(activity) {
        const date = moment(activity.startDate.date).utc().format('YYYY-MM-DD');
        return moment(date + activity.startDate.time, 'YYYY-MM-DDHH:mma');
    },

    getActivityEndDateTime(activity) {
        const date = moment(activity.endDate.date).utc().format('YYYY-MM-DD');
        return moment(date + activity.endDate.time, 'YYYY-MM-DDHH:mma');
    },

    getActivityMeetingDuration(activity) {
        const endDateTime = this.getActivityEndDateTime(activity);
        const startDateTime = this.getActivityStartDateTime(activity);

        return endDateTime.diff(startDateTime, 'seconds');
    },

    isActivityCoreType(activity) {
        return ['Call', 'Email', 'Note', 'Meeting'].includes(activity.type.value);
    },

    getActivityDate(activity) {
        return `${moment(activity.startDate.date).format('YYYY-MM-DD')}T${moment(activity.startDate.time, 'HH:mma').utc().format('HH:mm:ss')}Z`;
    },

    addMeetingDetails(activity, payload) {
        const activityDate = this.getActivityDate(activity);

        payload.ContactId = null;
        payload.UserId = null;
        payload.ActivityDate = activityDate;
        payload.MeetingSubject = activity.subject;
        payload.MeetingLocation = activity.location;
        payload.MeetingStatus = activity.status;
        payload.MeetingDuration = this.getActivityMeetingDuration(activity);
        payload.Participants = activity.participants.map((participant) => {
            if (participant.type === 'User') {
                return {
                    ContactId: null, UserId: participant.id
                };
            } else if (participant.type === 'Contact') {
                return {
                    ContactId: participant.id, UserId: null
                };
            }
        });

        if (activity.contact) {
            payload.Participants.push({
                ContactId: activity.contact.id
            });
        }
    },

    postActivity(activity, options = {}) {
        const { presetDealId } = options;
        const userId = store.state.user.id;
        const isCoreType = this.isActivityCoreType(activity);

        let payload = {
            ActivityDate: moment.utc().format('YYYY-MM-DDTHH:mm:ssZ'),
            ContactId: activity.contact.id,
            SalesNote: activity.description,
            UserId: userId
        };

        if (presetDealId || activity.opportunity) {
            payload.DealId = presetDealId || activity.opportunity.id;
        }

        if (this.isActivityMeetingType(activity)) {
            this.addMeetingDetails(activity, payload);
        }

        let restUrl;

        return new Promise((resolve, reject) => {
            if (isCoreType) {
                restUrl = `/api/${activity.type.value}Activities`;

                api
                    .post(restUrl, payload)
                    .then(resolve)
                    .catch(reject);
            } else {
                restUrl = '/api/CustomActivities';
                payload = {
                    ContactId: payload.ContactId,
                    Description: payload.SalesNote,
                    CustomActivityTypeId: activity.type.id,
                    UserId: userId
                };

                api
                    .post(restUrl, payload)
                    .then(resolve)
                    .catch(reject);
            }
        });
    },

    addMeetingChanges(activity, changes) {
        var participants = activity.participants.map((participant) => {
            if (participant.type === 'User') {
                return {
                    ContactId: null, UserId: participant.id
                };
            } else if (participant.type === 'Contact') {
                return {
                    ContactId: participant.id, UserId: null
                };
            }
        });

        if (activity.contact) {
            participants.push({
                ContactId: activity.contact.id, UserId: null
            });
        }

        const activityDate = `${moment(activity.startDate.date).format('YYYY-MM-DD')}T${moment(activity.startDate.time, 'HH:mma').utc().format('HH:mm:ss')}Z`;

        const meetingDuration = this.getActivityMeetingDuration(activity);

        changes.push({
            name: 'ActivityDate', value: activityDate
        }, {
            name: 'MeetingSubject', value: activity.subject
        }, {
            name: 'MeetingLocation', value: activity.location
        }, {
            name: 'MeetingStatus', value: activity.status
        }, {
            name: 'MeetingDuration', value: meetingDuration
        }, {
            name: 'Participants', value: participants
        });
    },

    patchActivity(activity) {
        const isCoreType = this.isActivityCoreType(activity);

        let restUrl;
        let changes;

        return new Promise((resolve, reject) => {

            if (isCoreType) {
                restUrl = `/api/${activity.type.value}Activities/${activity.id}`;
                changes = [{
                    name: 'SalesNote', value: activity.description
                }];

                if (activity.opportunity) {
                    changes.push({
                        name: 'DealId', value: activity.opportunity.id
                    });
                }

                if (this.isActivityMeetingType(activity)) {
                    this.addMeetingChanges(activity, changes);
                }

                api
                    .patch(restUrl, changes)
                    .then(resolve)
                    .catch(reject);
            } else {
                restUrl = `/api/CustomActivities/${activity.id}`;

                changes = [
                    {
                        name: '/Description',
                        value: activity.description,
                    },
                ];

                api
                    .patch(restUrl, changes)
                    .then(resolve)
                    .catch(reject);
            }
        });

    },

    getActivityFetchUrlQuery({ contactId, dealId, pageSize, skippedRecords, type, search, userFilter }) {
        let filter = [];

        if (search) {
            var encodedSearch = encodeURIComponent(search);
            var searchQuery = `contains(concat(concat(Contact/FirstName, ' '), Contact/LastName), '${encodedSearch}') or contains(Contact/Email, '${encodedSearch}') or contains(Contact/Company/Name, '${encodedSearch}')`;

            if (type === 'Calls' || type === 'Follow Up Emails' || type === 'Contact Emails' || type === 'SMS' || type === 'Notes' || type === 'Meetings') {
                searchQuery += ` or contains(concat(concat(User/FirstName, ' '), User/LastName), '${encodedSearch}') or contains(User/Email, '${encodedSearch}')`;
            }

            filter.push(`(${searchQuery})`);
        }

        if (contactId) {
            filter.push(`(ContactId eq ${contactId} or ActivityParticipants/any(p: p/ContactId eq ${contactId}))`);
            filter.push('DealId eq null');
        } else if (dealId) {
            filter.push(`DealId eq ${dealId}`);
        }

        let idQuery = '';
        if (contactId) {
            idQuery = `contactId=${contactId}`;
        } else if (dealId) {
            idQuery = `dealId=${dealId}`;
        }

        if (userFilter && userFilter.length) {
            const userFilterString = userFilter
                .map(({ key }) => `UserId eq '${key}'`)
                .join(' or ');
            filter.push(userFilterString);
        }

        switch (type) {
            case 'Calls':
                filter.push('Type eq \'CallActivity\'');
                break;

            case 'Bounce':
                filter.push('Type eq \'BounceActivity\'');
                break;

            case 'Follow Up Emails':
            case 'Contact Emails':
                filter.push('Type eq \'EmailActivity\'');
                break;

            case 'Opened Emails':
                filter.push('Type eq \'OpenActivity\'');
                break;

            case 'Clicked Emails':
                filter.push('Type eq \'ClickActivity\'');
                break;

            case 'Notes':
                filter.push('Type eq \'NoteActivity\'');
                break;

            case 'Meetings':
                filter.push('Type eq \'MeetingActivity\'');
                break;

            case 'Form Submissions':
                filter.push('Type eq \'FormActivity\'');
                break;

            case 'Unsubscribes':
                filter.push('Type eq \'UnsubscribeActivity\'');
                break;

            case 'SMS':
                filter.push('Type eq \'SmsActivity\'');
                break;

            case 'Custom':
                filter.push('Type eq \'CustomActivity\'');
                break;
        }

        const typeOptionsUrls = {
            Calls: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=ProfileImageUrl,FirstName,LastName,Email)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            Bounce: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=Contact($select=ProfileImageUrl,FirstName,LastName,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($select=Subject,From)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            'Follow Up Emails': {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=ProfileImageUrl,FirstName,LastName,Email)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            'Contact Emails': {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=ProfileImageUrl,FirstName,LastName,Email)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')} and UserId eq null&$top=${pageSize}&$skip=${skippedRecords}`
            },

            'Opened Emails': {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=Contact($select=ProfileImageUrl,FirstName,LastName,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($select=Subject,From)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            'Clicked Emails': {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=Contact($select=ProfileImageUrl,FirstName,LastName,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($select=Subject,From)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            Notes: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=User($select=FirstName,LastName,ProfileImageUrl,Email)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            Meetings: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$filter=${filter.join(' and ')}&$expand=ActivityParticipants($expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=Id,ProfileImageUrl,FirstName,LastName,Email,MobilePhone)),Contact($select=ProfileImageUrl,FirstName,LastName,Email,MobilePhone),Sms($expand=Participants),User($select=FirstName,LastName,ProfileImageUrl,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($expand=JourneyActions($select=Id);$select=Subject,From),UnsubscribeCampaign($select=Id,Name,PreviewImageUrl,FromEmail),Contact($select=ProfileImageUrl,FirstName,LastName,Email),RegisteredMeetings($select=Id;$expand=Agency($select=Id,Name)),CustomActivityType($select=Id,Name,IconColor,Icon)&$top=${pageSize}&$skip=${skippedRecords}&$orderby=ActivityDate desc`
            },

            'Form Submissions': {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=Contact($select=ProfileImageUrl,FirstName,LastName,Email)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            Unsubscribes: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=Contact($select=ProfileImageUrl,FirstName,LastName,Email),UnsubscribeCampaign($select=Id,Name,PreviewImageUrl,FromEmail)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            SMS: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$expand=User($select=ProfileImageUrl,FirstName,LastName),Contact($select=Id,ProfileImageUrl,FirstName,LastName,MobilePhone),Sms($expand=Participants)&$orderby=ActivityDate desc&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}`
            },

            Custom: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$top=${pageSize}&$skip=${skippedRecords}&$filter=${filter.join(' and ')}&$expand=Deal($select=Id,Name),ActivityParticipants($expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=Id,ProfileImageUrl,FirstName,LastName,Email,MobilePhone)),Contact($select=ProfileImageUrl,FirstName,LastName,Email,MobilePhone),Sms($expand=Participants),User($select=FirstName,LastName,ProfileImageUrl,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($expand=JourneyActions($select=Id);$select=Subject,From),UnsubscribeCampaign($select=Id,Name,PreviewImageUrl,FromEmail),Contact($select=ProfileImageUrl,FirstName,LastName,Email),RegisteredMeetings($select=Id;$expand=Agency($select=Id,Name)),CustomActivityType($select=Id,Name,IconColor,Icon)&$orderby=ActivityDate desc`
            },

            All: {
                url: '/service/ActivitiesExtended',
                fetchQuery: `${idQuery}&$filter=${filter.join(' and ')}&$top=${pageSize}&$skip=${skippedRecords}&$expand=ActivityParticipants($expand=User($select=FirstName,LastName,ProfileImageUrl,Email),Contact($select=Id,ProfileImageUrl,FirstName,LastName,Email,MobilePhone)),Contact($select=ProfileImageUrl,FirstName,LastName,Email,MobilePhone),Sms($expand=Participants),User($select=FirstName,LastName,ProfileImageUrl,Email),Campaign($select=Id,Name,PreviewImageUrl,FromEmail),Message($expand=JourneyActions($select=Id);$select=Subject,From),UnsubscribeCampaign($select=Id,Name,PreviewImageUrl,FromEmail),Contact($select=ProfileImageUrl,FirstName,LastName,Email),RegisteredMeetings($select=Id;$expand=Agency($select=Id,Name)),CustomActivityType($select=Name,IconColor,Icon)&$orderby=ActivityDate desc`
            }
        };
        return [typeOptionsUrls[type].url, typeOptionsUrls[type].fetchQuery].join('?');
    },

    getActivities({
                      contactId,
                      dealId,
                      pageSize,
                      skippedRecords,
                      type = 'All',
                      search,
                      userFilter,
                      filteredActivityType
                  }, abortSignal = null) {
        const activitiesEndpoint = this.getActivityFetchUrlQuery({
            contactId, dealId, pageSize, skippedRecords, type, search, userFilter
        });

        return new Promise((resolve, reject) => {
            api
                .get(activitiesEndpoint, abortSignal)
                .then((response) => {
                    const phoneIds = Array.from(new Set(response.data.value
                        .filter((response) => response['@odata.type'] === '#TriDigital.EntityDomain.Entities.SmsActivity')
                        .map((response) => {
                            return response.Sms.Participants.length > 0 ? response.Sms.Participants[0].PhoneNumberId : null;
                        })
                        .filter((x) => x)));

                    const needsExtraInformation = phoneIds.length > 0;
                    if (!needsExtraInformation) {
                        resolve(this.buildActivities(response, type, filteredActivityType));
                    } else {
                        const service = '/service/PhoneNumbers';
                        const filter = `$filter=${phoneIds
                            .map((id) => `Id eq ${id}`)
                            .join(' or ')}`;
                        const expand = '$expand=User($select=FirstName,LastName)';

                        const queryParams = [filter, expand].join('&');

                        api
                            .get([service, queryParams].join('?'), abortSignal)
                            .then((response) => {
                                const smsPhonesData = response.data.value;
                                const extraInformation = {
                                    smsPhonesData
                                };

                                resolve(this.buildActivities(response, type, filteredActivityType, extraInformation));
                            })
                            .catch(reject);
                    }
                })
                .catch(reject);
        });
    },

    buildActivities(response, type, filteredActivityType, extraInformation = null) {
        const newActivities = response.data.value
            .map((activity) => this.createActivity(activity, extraInformation, type, filteredActivityType))
            .filter((activity) => activity.type !== 'Delivered' && activity.type !== 'SpamReport' && activity.type !== 'Custom');
        var groups = [];
        var currentGroup = null;

        newActivities.forEach((activity, index) => {
            if (!currentGroup) {
                currentGroup = this.createActivityGroup(activity);
            } else {
                var canActivityBeGrouped = activity.type === 'Open' || activity.type === 'Click';
                var belongsInActivityGroup = currentGroup.key === this.getActivityGroupKey(activity);

                if (currentGroup.isGroupable && canActivityBeGrouped && belongsInActivityGroup) {
                    currentGroup.activities.push(activity);
                } else {
                    this.updateGroupCounts(currentGroup);
                    groups.push(currentGroup);
                    currentGroup = this.createActivityGroup(activity);
                }
            }

            var isLastActivity = index === newActivities.length - 1;
            if (isLastActivity && currentGroup) {
                this.updateGroupCounts(currentGroup);
                groups.push(currentGroup);
            }
        });

        return { response, groups };
    },

    updateGroupCounts(group) {
        group.opens = group.activities.filter((a) => a.type === 'Open').length;
        group.clicks = group.activities.filter((a) => a.type === 'Click').length;
    },

    createActivityGroup(activity) {
        const type = this.getGroupType(activity);

        return {
            iconUrl: this.getIconSrc(type),
            hasPreview: this.getHasPreview(type),
            key: this.getActivityGroupKey(activity),
            vueKey: helpers.createGuid(),
            type: activity.customType || type,
            isGroupable: this.isGroupable(activity),
            isShown: false,
            activities: [activity],
            clicks: 0,
            opens: 0,
            deleting: false
        };
    },

    createActivity(responseValue, extraInformation, filterType, filteredActivityType) {
        const type = this.getActivityType(responseValue, filterType, filteredActivityType);
        const defaultParticipant = responseValue.ActivityParticipants && responseValue.ActivityParticipants.find((participant) => !!participant.ContactId);

        const activity = {
            id: responseValue.Id,
            actor: {
                name: null, profileImageUrl: null, type: null, email: null, phone: null
            },
            customType: responseValue.CustomActivityType && responseValue.CustomActivityType.Name,
            type,
            content: this.buildContent(responseValue, type, extraInformation),
            date: moment
                .utc(responseValue.ActivityDate || responseValue.CreatedAt)
                .local(),

            message: {
                id: responseValue.MessageId,
                subject: responseValue.Message ? responseValue.Message.Subject : null,
                fromEmail: responseValue.Message ? responseValue.Message.From : null
            },

            campaign: {
                id: responseValue.CampaignId,
                name: responseValue.Campaign ? responseValue.Campaign.Name : null,
                fromEmail: responseValue.Campaign ? responseValue.Campaign.FromEmail : null,
                previewImageUrl: responseValue.Campaign && responseValue.Campaign.PreviewImageUrl ? responseValue.Campaign.PreviewImageUrl : require('@/assets/images/template-preview.png')
            },

            contactId: responseValue.ContactId || defaultParticipant && defaultParticipant.ContactId
        };

        activity.actor = this.getActor(responseValue);

        return activity;
    },

    buildContent(responseValue, type, extraInformation) {
        var formName = '';
        if (responseValue.LandingPageFormSubmissionId) {
            const config = {
                expand: 'LandingPage, Form'
            };
            LandingPageFormSubmissionService.get(responseValue.LandingPageFormSubmissionId, config)
                .then(({ data }) => {
                    formName = data.Form.Name;
                })
                .catch((error) => {
                    helpers.showFailNotification('An error occurred while getting your form submission information.', { error });
                });
        }

        var registeredMeetings = '';
        if (responseValue.RegisteredMeetings) {
            registeredMeetings = responseValue.RegisteredMeetings.map(({ Agency }) => Agency.Name).join(', ');
        }

        var receiverName = '';
        var receiverNumber = '';
        var senderNumber = '';

        if (responseValue.Sms && responseValue.Sms.Participants.length > 0) {
            const { smsPhonesData } = extraInformation;
            const phoneData = smsPhonesData.find((phone) => phone.Id === responseValue.Sms.Participants[0].PhoneNumberId);

            const participants = responseValue.Sms.Participants;

            const [currentUser] = participants.filter((participant) => participant.ContactId === null);

            const userName = helpers.getName(phoneData.User.FirstName, phoneData.User.LastName);
            const userNumber = phoneData.Number;

            const isContactSender = !(currentUser.SmsParticipantType === 'Sender');

            senderNumber = isContactSender ? userNumber : responseValue.Contact.MobilePhone;
            receiverNumber = isContactSender ? userNumber : responseValue.Contact.MobilePhone;
            receiverName = isContactSender ? userName : helpers.getName(responseValue.Contact.FirstName, responseValue.Contact.LastName);
        }

        switch (type) {
            case 'Email':
            case 'Click':
            case 'Open':
                return responseValue.Message ? `<b>Subject:</b><br>${responseValue.Message.Subject}` : (responseValue.SalesNote || null);
            case 'Call':
            case 'Note':
            case 'Company Notes':
            case 'HoneyBot Note':
                return responseValue.SalesNote || responseValue.Description;
            case 'Meeting':
                return `${responseValue.MeetingStatus ? `<b>Status</b><br>${responseValue.MeetingStatus}<br>` : ''}
                    ${responseValue.salesNote ? `<b>Description</b><br>${responseValue.salesNote}<br>` : ''}
                    ${responseValue.MeetingDuration ? `<b>Duration</b><br>${moment.duration(responseValue.MeetingDuration, 'seconds').humanize()}<br>` : ''}
                    ${registeredMeetings ? `<b>Registered with</b><br>${registeredMeetings}` : ''}`;
            case 'Form':
                return `<b>${responseValue.FormType || 'General'}</b><br>${responseValue.FormComment || 'No comment added.'}`;
            case 'Form Submission':
                return `<b>${formName || responseValue.FormType || 'General'}</b><br>${responseValue.FormComment || 'No comment added.'}`;
            case 'Bounce':
            case 'Block':
                return responseValue.BounceReason || 'No reason given.';
            case 'Unsubscribe':
                return responseValue.UnsubscribeCampaign.Name;
            case 'Sms':
                return `<b>From:</b><br>${this.getActor(responseValue).name} (${senderNumber})<br><b>To:</b><br>${receiverName} (${receiverNumber})<br>${responseValue.Sms.Body}`;
            default:
                return responseValue.Description || responseValue.SalesNote || null;
        }
    },

    getActor(responseValue, contactId) {
        var actor = {
            name: null, profileImageUrl: null, type: null, email: null, phone: null
        };

        if (responseValue.User) {
            actor.name = helpers.getName(responseValue.User.FirstName, responseValue.User.LastName);
            actor.profileImageUrl = responseValue.User && responseValue.User.ProfileImageUrl || require('@/assets/images/avatar.png');
            actor.type = 'User';
            actor.email = responseValue.Message ? responseValue.Message.From : responseValue.User.Email;
        } else if (responseValue.Contact) {
            actor.profileImageUrl = responseValue.Contact && responseValue.Contact.ProfileImageUrl || require('@/assets/images/avatar.png');
            actor.name = helpers.getName(responseValue.Contact.FirstName, responseValue.Contact.LastName);
            actor.type = 'Contact';
            actor.email = responseValue.Contact.Email;
            actor.phone = responseValue.Contact.MobilePhone;
        } else if (responseValue.ActivityParticipants && responseValue.ActivityParticipants.length > 0) {
            var match = responseValue.ActivityParticipants.find((participant) => participant.ContactId === contactId);

            if (match && !match.Contact) {
                match = responseValue.ActivityParticipants.find((participant) => !!participant.ContactId);
            }

            actor.profileImageUrl = (match && match.Contact && match.Contact.ProfileImageUrl) || require('@/assets/images/avatar.png');
            actor.name = helpers.getName(match && match.Contact && match.Contact.FirstName, match && match.Contact && match.Contact.LastName);
            actor.type = 'Contact';
            actor.email = match && match.Contact && match.Contact.Email;
        }

        return actor;
    },

    getActivityType(responseValue, filterType, filteredActivityType) {
        var odataType = responseValue['@odata.type'];
        var type = responseValue.Type;

        if (odataType === '#TriDigital.EntityDomain.Entities.ClickActivity' || filterType === 'Clicked Emails' || type === 'ClickActivity') {
            return 'Click';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.OpenActivity' || filterType === 'Opened Emails' || type === 'OpenActivity') {
            return 'Open';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.FormActivity' || filterType === 'Form Submissions' || filteredActivityType === 'Form Submissions' || type === 'FormActivity') {
            return 'Form';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.UnsubscribeActivity' || filterType === 'Unsubscribes' || filteredActivityType === 'Unsubscribes' || type === 'UnsubscribeActivity') {
            return 'Unsubscribe';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.BounceActivity' || type === 'BounceActivity') {
            return 'Bounce';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.DeliveredActivity' || type === 'DeliveredActivity') {
            return 'Delivered';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.BlockActivity' || type === 'BlockActivity') {
            return 'Block';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.SpamReportActivity' || type === 'SpamReportActivity') {
            return 'SpamReport';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.CallActivity' || filterType === 'Calls' || type === 'CallActivity') {
            return 'Call';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.EmailActivity' || filterType === 'Follow Up Emails' || filterType === 'Contact Emails' || type === 'EmailActivity') {
            return 'Email';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.MeetingActivity' || filterType === 'Meetings' || type === 'MeetingActivity') {
            return 'Meeting';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.SmsActivity' || filterType === 'SMS' || type === 'SmsActivity') {
            return 'Sms';
        } else if (odataType === '#TriDigital.EntityDomain.Entities.CompanyNotesActivity' || type === 'CompanyNotesActivity') {
            return 'Company Notes';
        } else if (type === 'CustomActivity' || filteredActivityType === 'Custom') {
            return 'Custom';
        } else if (responseValue.UserId || type === 'NoteActivity') {
            return 'Note';
        } else {
            return 'HoneyBot Note';
        }
    },

    getGroupType(activity) {
        var type = '';

        if (activity.type === 'Click' || activity.type === 'Open') {
            if (activity.campaign.id) {
                type = 'Campaign Engagement';
            } else if (activity.journeyActionId) {
                type = 'Journey Engagement';
            } else {
                type = 'Email Engagement';
            }
        } else if (activity.type === 'Email') {
            if (activity.journeyActionId) {
                type = 'Journey Email';
            } else if (activity.actor.type === 'User') {
                type = 'Follow up Email';
            } else {
                type = 'Email Reply';
            }
        } else if (activity.type === 'Form' && activity.landingPageFormSubmissionId) {
            type = 'Form Submission';
        } else {
            type = activity.type;
        }

        return type;
    },

    getIconSrc(type) {

        switch (type) {
            case 'Follow up Email':
                return require('@/assets/images/SVG/message-in-svg.svg');
            case 'Email Reply':
                return require('@/assets/images/SVG/message-out-svg.svg');
            case 'Campaign Engagement':
            case 'Email Engagement':
                return require('@/assets/images/SVG/open-and-click-svg.svg');
            case 'Journey Email':
            case 'Journey Engagement':
                return require('@/assets/images/SVG/grid/s18-journey-activity-click.svg');
            case 'Call':
                return require('@/assets/images/SVG/Activities/call.svg');
            case 'Company Notes':
            case 'Note':
                return require('@/assets/images/SVG/Activities/chat.svg');
            case 'Meeting':
                return require('@/assets/images/SVG/Activities/meeting.svg');
            case 'Form':
            case 'Form Submission':
                return require('@/assets/images/SVG/Activities/form-submission.svg');
            case 'Bounce':
            case 'Block':
            case 'Unsubscribe':
                return require('@/assets/images/SVG/Activities/hand.svg');
            case 'HoneyBot Note':
                return require('@/assets/images/glasshive-icon-stroke.png');
            case 'Sms':
                return require('@/assets/images/SVG/grid/l4-sms-outline.svg');
            default:
                return null;
        }
    }, getHasPreview(type) {
        switch (type) {
            case 'Campaign Engagement':
            case 'Email Reply':
            case 'Email Engagement':
            case 'Journey Email':
            case 'Journey Engagement':
            case 'Follow up Email':
                return true;
            default:
                return false;
        }
    },

    getActivityGroupKey(activity) {
        var key = '';

        if (activity.campaign.id) {
            key = `campaign-${activity.campaign.id}`;
        } else if (activity.message.id) {
            key = `message-${activity.message.id}`;
        } else {
            key = `activity-${activity.id}`;
        }

        return key;
    },

    isGroupable(activity) {
        var isGroupableByCampaign = activity.campaign.id && (activity.type === 'Click' || activity.type === 'Open');

        var isGroupableByEmail = activity.message.id && (activity.type === 'Click' || activity.type === 'Open');

        return isGroupableByCampaign || isGroupableByEmail;
    }
};